import { appendCallHistoryCache, currentCallHistoryScope } from '../mobile/navigation/callHistoryStorage';
/**
 * Middleware for the interpreter queue feature.
 *
 * Connects the InterpreterQueueService WebSocket events to the Redux store,
 * enabling real-time updates for interpreter matching and queue status.
 */
import { appNavigate } from '../app/actions';
import { hangup } from '../base/connection/actions';
import { CONNECTION_ESTABLISHED, CONNECTION_FAILED } from '../base/connection/actionTypes';
import { DATA_CHANNEL_OPENED } from '../base/conference/actionTypes';
import { TRACK_ADDED, TRACK_UPDATED } from '../base/tracks/actionTypes';
import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE, SET_ROOM } from '../base/conference/actionTypes';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { getUserRole } from '../base/user-role/functions';
import { getBackendSafeRoomName } from '../base/util/uri';
import { getAppName, isVriApp } from '../base/whitelabel/functions';
import { updateSettings } from '../base/settings/actions';
import { APP_STATE_CHANGED } from '../mobile/background/actionTypes';
import { signOutMobileUser } from '../mobile/navigation/mobileLogout';
import { resetRoot } from '../mobile/navigation/rootNavigationContainerRef';
import { screen } from '../mobile/navigation/routes';
import { getPersistentItem, getPersistentJson, removePersistentItem, setPersistentItem } from '../vrs-auth/storage';
import { getSecureItem } from '../vrs-auth/secureStorage';
import { mobileLog } from '../mobile/navigation/logging';
import { emitMobileTelemetryEvent } from '../mobile/navigation/telemetry';
import { showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { apiClient } from '../shared/api-client';
import { MEDIA_REJOIN_RELOAD_SECONDS, isMediaSessionFailure, mediaFailureMessage, readMediaRejoinMarker, recordMediaFailure, writeMediaRejoinMarker } from './inCallMediaRejoin';
import { recordP2PInterpreterOnCall } from './p2pInterpreterEligibility';
import { isRecord, stringValue } from './queueServiceUtils';
import { beginInterpreterWrapUp, dismissInterpreterWrapUp, forgetWrapUpQueueCount, noteWrapUpQueueStatus } from './wrapUpActions';
import { getCallPeerLabel, getPhoneCallLabel } from '../../../shared/call-display-name';
import { queueService } from './InterpreterQueueService';
import { CANCEL_REQUEST, INTERPRETER_REQUEST_RECEIVED, MATCH_FOUND, QUEUE_SERVICE_ERROR, REQUEST_ACCEPTED, REQUEST_DECLINED, EXTERNAL_MEETING_CALL_ENDED, EXTERNAL_MEETING_CALL_UPDATED, MEETING_REMINDERS_ADDED, QUEUE_CAPABILITIES_UPDATED } from './actionTypes';
import { interpreterAvailabilityChanged, interpreterRequestReceived, interpretedCallEnded, matchFound, meetingInitiated, queueConnectionChanged, queueStatusUpdate } from './actions';
import { conferenceErrorDetails, isConferenceForActiveRoom, shouldAutoEnterMatchedRoom, shouldPreserveMatchedCallAfterExit } from './callLifecycleGuards';
import { getRoomNavigationTarget } from './roomNavigation';
import { meetingReminderSchema } from '../shared/api-client/meeting-reminders';
import { getClientStartWithVideoMuted } from './mediaDefaults';
import { clearIncomingP2PCall, incomingP2PCall } from '../call-management/actions';
import { JOIN_TIMELINE_KEY, parseJoinMarker, serializeJoinMarker } from './joinTimeline';
import { describeP2PUnavailable } from '../mobile/navigation/p2pUnavailableCopy';
import { declineNotice, groupNoAnswerText, groupRingGaps, isOtherParticipantDeparture, isPlatformCallRoomName, participantNoticeTitle, rememberGroupRinging } from '../vrs-layout/groupInvite';
import { isGroupRejoinPending } from './groupRejoin';
import { endedForAllNotice, rememberCallEndNotice } from './groupEndForAll';
import { moveNotice, watchHeldSession } from './accountDeviceNotices';
import { accountDevicePageActions, bindAccountDeviceDispatch, dismissAccountDeviceNotice, isAccountDeviceRoomPage, leaveConferenceWithoutEnding, presentAccountDeviceNotice } from './accountDevicePresenter';
let lastAutoEnteredQueueRoom;
/**
 * The platform (direct/group) call this browser last entered a room for.
 * Replayed join events for it are ignored, and a no-answer for it leaves
 * the room instead of parking the caller alone in it.
 */
let lastPlatformCall;
let callStartTimestamp;
let activeCallReconciliationInFlight = false;
let lastActiveCallReconciliationAt = 0;
let activeCallReconciliationTimer;
let conferenceExitInProgressKey;
let conferenceJoinedForActiveCall = false;
let joinedConferenceForActiveCall;
let conferenceJoinFailure;
let preJoinExitHandledKey;
let queueRoomNavigationStartedAt;
let queueRoomSignalingConnectedAt;
// The two marks after "joined" that tell a slow handshake apart from a slow
// signalling connect: media path up (data channel over the ICE/DTLS
// transport) and first remote video track. These locate a delay; they do
// not establish its cause without the corresponding connection evidence.
let queueRoomMediaConnectedAt;
let queueRoomRemoteVideoAt;
let queueRoomJoinSource;
let activeConferenceJoinedAt;
/**
 * A media-session failure of the joined call's conference, and whether it is
 * rejoined rather than ended (inCallMediaRejoin.ts). Recorded before the
 * reducer sees CONFERENCE_FAILED; the exit that follows reads it.
 */
let inCallMediaFailure;
/**
 * The room's latest camera change, reported with a failure: whether it came
 * right after a camera turned on is the Pixel incident's open question.
 */
let lastVideoChange;
/**
 * Report the first observed transport and connection/session transitions.
 * Multiple sessions can be an ordinary JVB/P2P transition, and transport
 * protocol alone does not establish the cause of a slow join.
 *
 * Raw lib-jitsi-meet event names are used on purpose: importing the event
 * enums here would drag the whole SDK into every sandboxed harness that
 * loads this file.
 */
const LJM_LOCAL_STATS_UPDATED = 'cq.local_stats_updated';
const LJM_CONNECTION_INTERRUPTED = 'conference.connectionInterrupted';
const LJM_CONNECTION_RESTORED = 'conference.connectionRestored';
const LJM_CONNECTION_ESTABLISHED = 'conference.connectionEstablished';
const LJM_MEDIA_SESSION_STARTED = 'conference.media_session.started';
let mediaTransportObserver;
function observeMediaTransport(conference, options) {
    const observable = conference;
    mediaTransportObserver?.dispose();
    if (typeof observable.on !== 'function' || typeof observable.off !== 'function') {
        return;
    }
    const joinedAt = Date.now();
    let active = true;
    let reported = false;
    const onStats = (...args) => {
        if (!active || reported) {
            return;
        }
        const stats = args[0];
        const raw = stats && typeof stats === 'object' && 'transport' in stats ? stats.transport : undefined;
        const candidateType = (value) => typeof value === 'string'
            && ['host', 'srflx', 'prflx', 'relay'].includes(value) ? value : undefined;
        const transports = (Array.isArray(raw) ? raw : []).slice(0, 3).flatMap((entry) => {
            if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
                return [];
            }
            const row = entry;
            const localCandidateType = candidateType(row.localCandidateType);
            const remoteCandidateType = candidateType(row.remoteCandidateType);
            const protocol = row.type === 'udp' || row.type === 'tcp' ? row.type : undefined;
            if (!localCandidateType && !remoteCandidateType && !protocol) {
                return [];
            }
            return [{
                    localCandidateType,
                    p2p: typeof row.p2p === 'boolean' ? row.p2p : undefined,
                    protocol,
                    relayed: localCandidateType === 'relay' || remoteCandidateType === 'relay',
                    remoteCandidateType,
                    rttMs: typeof row.rtt === 'number' && Number.isFinite(row.rtt) && row.rtt >= 0
                        ? Math.round(row.rtt) : undefined
                }];
        });
        if (!transports.length) {
            return;
        }
        reported = true;
        observable.off?.(LJM_LOCAL_STATS_UPDATED, onStats);
        emitMobileTelemetryEvent('vrs_media_transport', {
            sinceJoinMs: Math.max(0, Date.now() - joinedAt),
            // Candidate types and protocol only — never addresses.
            transports
        }, { ...options, state: 'media_connected' });
    };
    let sessions = 0;
    const onSessionStarted = (...args) => {
        if (!active) {
            return;
        }
        const session = args[0];
        const p2p = session && typeof session === 'object' && 'isP2P' in session
            && typeof session.isP2P === 'boolean' ? session.isP2P : undefined;
        sessions += 1;
        emitMobileTelemetryEvent('vrs_media_session_started', {
            index: sessions,
            p2p,
            sinceJoinMs: Math.max(0, Date.now() - joinedAt)
        }, { ...options, state: 'started' });
    };
    const onEstablished = () => active && emitMobileTelemetryEvent('vrs_ice_connected', {
        sessions,
        sinceJoinMs: Math.max(0, Date.now() - joinedAt)
    }, { ...options, state: 'ice_connected' });
    const onInterrupted = () => active && emitMobileTelemetryEvent('vrs_ice_interrupted', {
        sinceJoinMs: Math.max(0, Date.now() - joinedAt)
    }, { ...options, state: 'interrupted' });
    const onRestored = () => active && emitMobileTelemetryEvent('vrs_ice_restored', {
        sinceJoinMs: Math.max(0, Date.now() - joinedAt)
    }, { ...options, state: 'restored' });
    observable.on(LJM_LOCAL_STATS_UPDATED, onStats);
    observable.on(LJM_MEDIA_SESSION_STARTED, onSessionStarted);
    observable.on(LJM_CONNECTION_ESTABLISHED, onEstablished);
    observable.on(LJM_CONNECTION_INTERRUPTED, onInterrupted);
    observable.on(LJM_CONNECTION_RESTORED, onRestored);
    const observer = {
        conference,
        dispose: () => {
            active = false;
            observable.off?.(LJM_LOCAL_STATS_UPDATED, onStats);
            observable.off?.(LJM_MEDIA_SESSION_STARTED, onSessionStarted);
            observable.off?.(LJM_CONNECTION_ESTABLISHED, onEstablished);
            observable.off?.(LJM_CONNECTION_INTERRUPTED, onInterrupted);
            observable.off?.(LJM_CONNECTION_RESTORED, onRestored);
            if (mediaTransportObserver === observer) {
                mediaTransportObserver = undefined;
            }
        }
    };
    mediaTransportObserver = observer;
}
/**
 * On web the room is a full page load, so the navigation timestamp taken
 * above is gone by the time signaling connects. Whoever started the
 * navigation left a marker in localStorage; adopt it once for the room the
 * page is showing so the stage events measure from the tap, not from boot.
 */
function ensureJoinStartRestored() {
    if (queueRoomNavigationStartedAt || typeof window === 'undefined') {
        return;
    }
    const marker = parseJoinMarker(getPersistentItem(JOIN_TIMELINE_KEY), Date.now(), getCurrentBrowserRoomName());
    if (marker) {
        queueRoomNavigationStartedAt = marker.startedAt;
        queueRoomJoinSource = marker.source;
    }
}
/** Ms since this document started, for web stage events; absent on native. */
function sinceDocumentStartMs() {
    // `performance` is a browser global; the native lib does not declare it.
    const origin = globalThis.performance?.timeOrigin;
    return typeof origin === 'number' && Number.isFinite(origin) ? Math.max(0, Math.round(Date.now() - origin)) : undefined;
}
function rememberJoinStart(data, source) {
    if (!data.roomName) {
        return;
    }
    try {
        setPersistentItem(JOIN_TIMELINE_KEY, serializeJoinMarker({
            callId: data.callId,
            requestId: data.requestId,
            roomName: data.roomName,
            source,
            startedAt: Date.now()
        }));
    }
    catch { /* storage blocked: the stage events still measure from boot */ }
}
function resetConferenceLifecycleState() {
    conferenceExitInProgressKey = undefined;
    conferenceJoinedForActiveCall = false;
    joinedConferenceForActiveCall = undefined;
    conferenceJoinFailure = undefined;
    preJoinExitHandledKey = undefined;
    queueRoomNavigationStartedAt = undefined;
    queueRoomSignalingConnectedAt = undefined;
    queueRoomMediaConnectedAt = undefined;
    queueRoomRemoteVideoAt = undefined;
    queueRoomJoinSource = undefined;
    activeConferenceJoinedAt = undefined;
    inCallMediaFailure = undefined;
    lastVideoChange = undefined;
}
// A freshly matched call is protected from reconciler eviction for this long,
// covering the web client's full page navigation into the room before the
// server's active-call record is observable to the poll.
const ACTIVE_CALL_EVICTION_GRACE_MS = 20000;
const ACTIVE_CALL_RECONCILE_INTERVAL_MS = 5000;
const ACTIVE_CALL_RECONCILE_LOOP_MS = 7000;
const VRI_LAST_CALL_END_NOTICE_KEY = 'vri_last_call_end_notice';
function getStoredActiveCall() {
    return getPersistentJson('vrs_active_call');
}
/**
 * The call a room stage event (signaling, media, first remote video) belongs
 * to. The stored call (vrs_active_call) is in localStorage and outlives its
 * call, so on a room page it counts only when it names that room; the join
 * marker for the room, written by the tap that opened it, comes first, as for
 * the room boot and load events (roomBootTelemetry.web.ts). A stale stored call split one join
 * into three timeline rows: boot by room, load by the new call, and these
 * stages by the old call with the old room's name (2026-09-23 review). Native
 * has no page path, so the stored call is used there as before.
 *
 * @returns {Object} The callId, requestId and roomName to report.
 */
function getStageEventCall() {
    const activeCall = getStoredActiveCall();
    const pageRoom = getCurrentBrowserRoomName();
    if (!pageRoom) {
        return {
            callId: activeCall?.callId,
            requestId: activeCall?.requestId,
            roomName: activeCall?.roomName
        };
    }
    const marker = parseJoinMarker(getPersistentItem(JOIN_TIMELINE_KEY), Date.now(), pageRoom);
    const roomCall = activeCall?.roomName
        && getBackendSafeRoomName(activeCall.roomName) === getBackendSafeRoomName(pageRoom) ? activeCall : undefined;
    return {
        callId: marker?.callId || roomCall?.callId,
        requestId: marker?.requestId || roomCall?.requestId,
        roomName: marker?.roomName || roomCall?.roomName || pageRoom
    };
}
function getActiveCallExitKey(activeCall) {
    return activeCall?.callId || activeCall?.roomName;
}
function getStoredQueueUser() {
    const user = getPersistentJson('vrs_user_info');
    return {
        id: user?.id || user?.userId,
        role: (getPersistentItem('vrs_user_role') || user?.role || '').toLowerCase()
    };
}
function wasEndedByLocalClient(data, endedByRole) {
    const currentUser = getStoredQueueUser();
    const endedBy = typeof data.endedBy === 'string' ? data.endedBy.trim() : '';
    const endedBySelf = Boolean(currentUser.id && endedBy && currentUser.id === endedBy);
    return currentUser.role === 'client' && (endedByRole === 'client' || endedBySelf);
}
/**
 * Persists match metadata to storage so it's available when the call ends.
 * groupCall is the server's word that a platform call is a group, which lets
 * its host end it for everyone (groupEndForAll.ts).
 */
function persistMatchData(data) {
    setPersistentItem('vrs_active_call', JSON.stringify({
        callerIdPhone: data.callerIdPhone,
        callId: data.callId,
        callType: data.callType,
        clientId: data.clientId,
        clientStartWithVideoMuted: data.clientStartWithVideoMuted,
        groupCall: data.groupCall === true ? true : undefined,
        roomName: data.roomName,
        requestId: data.requestId,
        interpreterName: data.interpreterName,
        interpreterId: data.interpreterId,
        clientName: data.clientName,
        language: data.language,
        requestKind: data.requestKind,
        startedAt: Date.now(),
        targetPhone: data.targetPhone,
        targetPhoneLast4: data.targetPhoneLast4,
        vcoEnabled: data.vcoEnabled
    }));
    callStartTimestamp = Date.now();
    resetConferenceLifecycleState();
    emitMobileTelemetryEvent('queue_match_persisted', {
        hasCallId: Boolean(data.callId),
        hasRequestId: Boolean(data.requestId),
        hasRoomName: Boolean(data.roomName),
        language: data.language
    }, {
        callId: data.callId,
        requestId: data.requestId,
        roomName: data.roomName,
        state: 'matched'
    });
}
function hasStoredActiveCall() {
    const activeCall = getStoredActiveCall();
    return Boolean(activeCall?.callId || activeCall?.roomName);
}
function stopActiveCallReconciliationLoop() {
    if (!activeCallReconciliationTimer) {
        return;
    }
    clearInterval(activeCallReconciliationTimer);
    activeCallReconciliationTimer = undefined;
}
function startActiveCallReconciliationLoop(store) {
    if (activeCallReconciliationTimer || getPersistentItem('vrs_user_role') !== 'client') {
        return;
    }
    activeCallReconciliationTimer = setInterval(() => {
        if (!hasStoredActiveCall()) {
            stopActiveCallReconciliationLoop();
            return;
        }
        reconcileLocalActiveCall(store, 'active_call_timer_reconcile');
    }, ACTIVE_CALL_RECONCILE_LOOP_MS);
}
/**
 * Writes a local call history entry from the persisted active call data.
 */
function writeLocalCallHistory() {
    const activeCall = getStoredActiveCall();
    if (!activeCall?.callId) {
        return;
    }
    const duration = callStartTimestamp
        ? Math.round((Date.now() - callStartTimestamp) / 1000)
        : 0;
    const entry = {
        id: activeCall.callId,
        contactName: activeCall.clientName || 'Unknown',
        phoneNumber: '',
        direction: 'outgoing',
        duration,
        timestamp: new Date(activeCall.startedAt || Date.now()).toISOString(),
        interpreterName: activeCall.interpreterName
    };
    // Only the authenticated participant may own this local snapshot. An old
    // active-call record must not become the next account's recent history.
    const scope = currentCallHistoryScope();
    const participantId = scope?.owner.role === 'interpreter' ? activeCall.interpreterId : activeCall.clientId;
    if (scope && participantId === scope.owner.userId) {
        void appendCallHistoryCache(scope, entry).then(saved => {
            if (saved)
                mobileLog('info', 'call_history_entry_saved', { callId: entry.id, duration });
        });
    }
    else {
        mobileLog('warn', 'call_history_owner_unavailable');
    }
    callStartTimestamp = undefined;
}
function getLocalUserId() {
    const id = getPersistentJson('vrs_user_info')?.id;
    return typeof id === 'string' ? id : undefined;
}
/**
 * Names of the people in the current platform call, by account id, learned
 * from the ring, answer and join events. p2p_participant_left carries only
 * the id; "Someone left" is the fallback, never a wrong name.
 */
const platformCallPeerNames = new Map();
/**
 * Stand-ins the server sends when it knows no name: 'Unknown' (p2p_join_room
 * from a server older than the null fallback) and 'Malka contact' (the ring
 * and push fallback). They name nobody, so they are never remembered or
 * stored as a person (the room's partner pane would greet them as one).
 */
const PLACEHOLDER_PLATFORM_PEER_NAMES = new Set(['unknown', 'malka contact']);
function usablePlatformPeerName(name) {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return trimmed && !PLACEHOLDER_PLATFORM_PEER_NAMES.has(trimmed.toLowerCase()) ? trimmed : undefined;
}
function rememberPlatformCallPeer(callId, userId, name) {
    const usable = usablePlatformPeerName(name);
    if (callId && userId && usable) {
        platformCallPeerNames.set(`${callId}:${userId}`, usable);
    }
}
/**
 * The other person's name for a direct call being entered: the one the ring
 * carried (the server's own record of the caller) first, then the name this
 * event offers, if it is a real one.
 */
function platformCallPeerName(callId, userId, offered) {
    const remembered = callId && userId ? platformCallPeerNames.get(`${callId}:${userId}`) : undefined;
    return remembered || usablePlatformPeerName(offered);
}
function isCurrentPlatformCall(callId) {
    const activeCall = getStoredActiveCall();
    return Boolean(callId && activeCall?.callId === callId && isPlatformCallRoomName(activeCall.roomName));
}
/**
 * The server says the current platform call is a group now: someone joined
 * it (p2p_participant_joined — a direct call becomes a group when a third
 * person is invited), or an answer said so (p2p_accepted.groupCall). Its
 * host may then end it for everyone. Never for another call.
 */
function markPlatformCallGroup(callId) {
    const activeCall = getPersistentJson('vrs_active_call');
    if (!callId || !activeCall || activeCall.callId !== callId || activeCall.groupCall === true
        || !isPlatformCallRoomName(typeof activeCall.roomName === 'string' ? activeCall.roomName : undefined)) {
        return;
    }
    setPersistentItem('vrs_active_call', JSON.stringify({ ...activeCall, groupCall: true }));
}
/**
 * "Dana joined" / "Dana left" for everyone in a platform call. Deaf
 * participants have no audio cue that someone came or went, and platform
 * rooms get no Jitsi join/leave toasts (joinLeaveNotificationsDisabled).
 */
function showPlatformParticipantNotice(store, kind, data) {
    if (!data.userId || data.userId === getLocalUserId() || !isCurrentPlatformCall(data.callId)) {
        return;
    }
    rememberPlatformCallPeer(data.callId, data.userId, data.name);
    store.dispatch(showNotification({
        titleKey: undefined,
        title: participantNoticeTitle(kind, data.name || platformCallPeerNames.get(`${data.callId}:${data.userId}`), { rejoined: data.rejoined, removed: data.removed }),
        descriptionKey: undefined,
        uid: `p2p-participant-${kind}-${data.userId}`
    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
/**
 * The caller auto-enters the room on ringing (native), and a group caller
 * is in the room while others still ring, so a decline must be surfaced IN
 * the room. In a group call one decline changes nothing for everyone else:
 * it says who declined and keeps the call.
 */
function showP2PDeclinedNotice(store, data) {
    const roomName = getStoredActiveCall()?.roomName || getCurrentBrowserRoomName();
    const notice = declineNotice(data.calleeName, Boolean(roomName?.startsWith('group-')), store.getState()['features/base/participants'].remote.size > 0);
    store.dispatch(showNotification({
        titleKey: undefined,
        title: notice.title,
        descriptionKey: undefined,
        description: notice.description,
        uid: `p2p-declined-${data.calleeId || 'peer'}`
    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
}
/**
 * Nobody answered a group call. On web the caller is still on Home, which
 * closes its ringing bar; a caller parked alone in the room (native enters
 * on ringing) is told here, and told apart: "Everyone declined your call."
 * or "No one answered." The call_ended the server sends next closes the
 * room. Never for another call.
 */
function handleP2PGroupNoAnswer(store, data) {
    if (!data.callId || (lastPlatformCall?.callId !== data.callId && !isCurrentPlatformCall(data.callId))) {
        return;
    }
    store.dispatch(showNotification({
        titleKey: undefined,
        title: groupNoAnswerText(data),
        descriptionKey: undefined,
        uid: 'p2p-group-no-answer'
    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
}
/**
 * A group call is ringing. The native caller goes straight into the room,
 * so the room's invite panel starts from this first round, and the caller
 * is told there who is not being rung: handles or emails that matched no
 * one, and people with Do Not Disturb on. The web caller waits on Home,
 * whose ringing bar says the same.
 */
function handleGroupRinging(store, data) {
    if (data.groupCall !== true) {
        return;
    }
    rememberGroupRinging(data);
    const gaps = groupRingGaps(data);
    if (gaps && isNativeRuntime()) {
        store.dispatch(showNotification({
            titleKey: undefined,
            title: gaps,
            descriptionKey: undefined,
            uid: `p2p-group-ring-gaps-${data.callId || 'call'}`
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    }
}
function handleLinkedInterpretedCallEnded(store, data) {
    // A group participant's own departure reaches them as call_ended naming
    // them. Anyone else holding that record stays in the call: only a real
    // end of the call ends the room, never one person leaving.
    if (isOtherParticipantDeparture(data, getLocalUserId())) {
        mobileLog('info', 'platform_call_departure_ignored', { callId: data.callId }, { console: false });
        return;
    }
    const endedByRole = typeof data.endedByRole === 'string'
        ? data.endedByRole
        : data.endedBy === 'admin' ? 'admin' : undefined;
    const endReason = typeof data.endReason === 'string'
        ? data.endReason
        : typeof data.reason === 'string' ? data.reason : undefined;
    const activeCall = getPersistentJson('vrs_active_call');
    const eventCallId = data.callId?.trim();
    const eventRoomName = getQueueRoomName(data);
    const callIdMatches = Boolean(activeCall?.callId && eventCallId && activeCall.callId === eventCallId);
    const roomNameMatches = Boolean(activeCall?.roomName && eventRoomName && activeCall.roomName === eventRoomName);
    if (!callIdMatches && !roomNameMatches) {
        return;
    }
    writeLocalCallHistory();
    if (wasEndedByLocalClient(data, endedByRole)) {
        removePersistentItem(VRI_LAST_CALL_END_NOTICE_KEY);
    }
    else {
        setPersistentItem(VRI_LAST_CALL_END_NOTICE_KEY, JSON.stringify({
            endedAt: Date.now(),
            message: endedByRole === 'admin'
                ? 'The session was ended by an administrator.'
                : endedByRole === 'interpreter'
                    ? 'The interpreter ended the session.'
                    : endedByRole === 'client'
                        ? 'The client ended the session.'
                        : 'The session ended.',
            reason: endReason || endedByRole || 'remote_call_ended'
        }));
    }
    removePersistentItem('vrs_active_call');
    removePersistentItem('vri_pending_invite_tokens');
    removePersistentItem('vri_pending_invite_url');
    removePersistentItem('vri_pending_invite_draft');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    resetConferenceLifecycleState();
    forgetMediaRejoin();
    // Say who ended it, out loud, on the device.
    //
    // This was telemetry-only, so answering "what hung that call up?" needed
    // the admin panel — and when the panel is unreachable, or the row's payload
    // is not expandable, the answer was unavailable at exactly the moment it
    // was wanted. endedBy/endedByRole/surface now also reach the browser
    // console and the mobile log buffer, where a user can read them back
    // without any operator access at all.
    const endDetails = {
        callId: activeCall?.callId || eventCallId,
        endedBy: typeof data.endedBy === 'string' ? data.endedBy : undefined,
        endedByRole,
        reason: endReason || endedByRole || 'remote_call_ended',
        roomName: activeCall?.roomName || eventRoomName,
        surface: typeof data.surface === 'string' ? data.surface : undefined
    };
    mobileLog('warn', 'interpreted_call_ended_remote', endDetails);
    emitMobileTelemetryEvent('interpreted_call_ended_remote', {
        endedBy: endDetails.endedBy,
        endedByRole,
        reason: endDetails.reason,
        surface: endDetails.surface
    }, {
        callId: endDetails.callId,
        roomName: endDetails.roomName,
        state: 'ended'
    });
    // The host ended a group call for everyone: "Dana ended the call for
    // everyone." The room closes at once, so Home shows it too.
    const forAllNotice = endedForAllNotice(data, getLocalUserId());
    if (forAllNotice) {
        rememberCallEndNotice(activeCall?.callId || eventCallId, forAllNotice);
        store.dispatch(showNotification({
            titleKey: undefined,
            title: forAllNotice,
            descriptionKey: undefined,
            uid: 'p2p-ended-for-all'
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    }
    beginInterpreterWrapUp(store, activeCall, { native: isNativeRuntime() });
    store.dispatch(interpretedCallEnded(endReason || endedByRole || 'remote_call_ended'));
    store.dispatch(hangup(false));
    routeToRoleHome();
}
function clearLocalActiveCall(store, reason) {
    const activeCall = getStoredActiveCall();
    removePersistentItem('vrs_active_call');
    removePersistentItem('vri_pending_invite_tokens');
    removePersistentItem('vri_pending_invite_url');
    removePersistentItem('vri_pending_invite_draft');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    resetConferenceLifecycleState();
    forgetMediaRejoin();
    mobileLog('info', 'vrs_local_active_call_cleared', {
        callId: activeCall?.callId,
        reason,
        roomName: activeCall?.roomName
    });
    emitMobileTelemetryEvent('vrs_local_active_call_cleared', {
        reason
    }, {
        callId: activeCall?.callId,
        roomName: activeCall?.roomName,
        state: 'cleared'
    });
    beginInterpreterWrapUp(store, activeCall, { native: isNativeRuntime() });
    store.dispatch(interpretedCallEnded(reason));
    store.dispatch(hangup(false));
    routeToRoleHome();
}
function reconcileLocalActiveCall(store, reason) {
    const activeCall = getStoredActiveCall();
    if (!activeCall?.callId) {
        return;
    }
    if (getPersistentItem('vrs_user_role') !== 'client') {
        return;
    }
    const now = Date.now();
    if (activeCallReconciliationInFlight || now - lastActiveCallReconciliationAt < ACTIVE_CALL_RECONCILE_INTERVAL_MS) {
        return;
    }
    activeCallReconciliationInFlight = true;
    lastActiveCallReconciliationAt = now;
    const isCurrentCall = () => {
        const currentCall = getStoredActiveCall();
        return getPersistentItem('vrs_user_role') === 'client'
            && currentCall?.callId === activeCall.callId
            && currentCall?.roomName === activeCall.roomName;
    };
    apiClient.get('/api/client/active-call').then(response => {
        // This request may finish after a new match, local end or role change.
        // Its answer cannot clear or report a failure against that newer state.
        if (!isCurrentCall())
            return;
        if (response.error) {
            mobileLog('warn', 'vrs_active_call_reconcile_failed', {
                error: response.error,
                reason
            }, { console: false });
            return;
        }
        const serverCall = response.data?.call || null;
        const callIdMatches = Boolean(serverCall?.id && serverCall.id === activeCall.callId);
        const roomNameMatches = Boolean(serverCall?.room_name && serverCall.room_name === activeCall.roomName);
        if (!serverCall || (!callIdMatches && !roomNameMatches)) {
            // Never evict a call that has only just started. The web client
            // reaches the room through a full page navigation, so there is a
            // window where the browser is already in the room while the
            // server's active-call record is not yet visible to this poll —
            // clearing there would hang up a perfectly good call and bounce
            // the client home. Native stores startedAt as a number, web as an
            // ISO string; accept both and fail safe (skip) if unparseable.
            const startedAtRaw = activeCall.startedAt;
            const startedAt = typeof startedAtRaw === 'number'
                ? startedAtRaw
                : Date.parse(String(startedAtRaw ?? ''));
            if (!Number.isFinite(startedAt) || Date.now() - startedAt < ACTIVE_CALL_EVICTION_GRACE_MS) {
                return;
            }
            clearLocalActiveCall(store, reason);
        }
    }).catch(() => {
        if (isCurrentCall()) {
            mobileLog('warn', 'vrs_active_call_reconcile_failed', {
                error: 'ACTIVE_CALL_RECONCILE_REQUEST_FAILED',
                reason
            }, { console: false });
        }
    }).finally(() => {
        activeCallReconciliationInFlight = false;
    });
}
/**
 * Send the user back to their role's home surface after a call ends or its
 * local state is cleared.
 *
 * resetRoot is React Navigation, which exists only in the native app: on web
 * rootNavigationRef.current is null, so every call here returned undefined and
 * navigated NOWHERE. That silence is what made the web client loop — the
 * reconciler would hang up the call and delete vrs_active_call, then leave the
 * browser sitting on the room URL with no call, where the conference promptly
 * tried to re-establish itself and the cycle repeated until the tab was
 * closed. Native clients were unaffected because for them resetRoot works, so
 * the same code path recovered cleanly. Web needs a real navigation.
 */
function isNativeRuntime() {
    return typeof navigator !== 'undefined'
        && navigator.product === 'ReactNative';
}
function routeToRoleHome() {
    const userRole = getPersistentItem('vrs_user_role');
    const isNative = isNativeRuntime();
    if (!isNative && typeof window !== 'undefined' && window.location) {
        // Same role → home mapping the web sign-in uses (WelcomePage.web).
        window.location.href = userRole === 'interpreter'
            ? '/interpreter-profile.html'
            : userRole === 'captioner'
                ? '/captioner-profile.html'
                : '/';
        return;
    }
    if (userRole === 'interpreter') {
        resetRoot(screen.interpreter.home);
        return;
    }
    resetRoot(isVriApp() ? screen.vri.console : screen.vrs.home);
}
function finalizeConferenceExit(store, reason) {
    const activeCall = getStoredActiveCall();
    const exitKey = getActiveCallExitKey(activeCall);
    if (!exitKey || conferenceExitInProgressKey === exitKey) {
        return;
    }
    conferenceExitInProgressKey = exitKey;
    writeLocalCallHistory();
    queueService.endActiveCall();
    removePersistentItem('vrs_active_call');
    removePersistentItem('vrs_own_queue_request');
    removePersistentItem('vri_pending_invite_tokens');
    removePersistentItem('vri_pending_invite_url');
    removePersistentItem('vri_pending_invite_draft');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    forgetMediaRejoin();
    mobileLog('info', 'vrs_conference_exit_finalized', { reason }, { console: false });
    emitMobileTelemetryEvent('vrs_conference_exit_finalized', { reason }, {
        callId: activeCall?.callId,
        requestId: activeCall?.requestId,
        roomName: activeCall?.roomName,
        state: 'exiting'
    });
    resetConferenceLifecycleState();
    beginInterpreterWrapUp(store, activeCall, { native: isNativeRuntime() });
    store.dispatch(interpretedCallEnded(reason));
    store.dispatch(hangup(false));
    routeToRoleHome();
}
/**
 * Whether a call-move message is about the call on this device: the stored
 * call, or (web) the room this page is showing.
 */
function callMoveBelongsHere(data) {
    const activeCall = getStoredActiveCall();
    if (data.callId && activeCall?.callId) {
        return activeCall.callId === data.callId;
    }
    const pageRoom = getCurrentBrowserRoomName();
    return Boolean(data.roomName && pageRoom
        && getBackendSafeRoomName(data.roomName) === getBackendSafeRoomName(pageRoom));
}
/**
 * The client's call moved to another of their devices (call_moved_elsewhere).
 * Leave the room WITHOUT ending the call: the local call record goes first,
 * so the conference-exit path finds nothing to finalize and sends no
 * call_end (the queue service also refuses call_end for a moved call), then
 * say where the call went. "Move it back here" restores the record and
 * reopens this room asking the server to move the call back.
 */
function leaveMovedCall(store, data) {
    if (!callMoveBelongsHere(data)) {
        return;
    }
    const activeCall = getStoredActiveCall();
    const saved = activeCall && (!data.callId || activeCall.callId === data.callId) ? activeCall : null;
    const pageActions = accountDevicePageActions();
    const moveBack = pageActions.moveBack;
    removePersistentItem('vrs_active_call');
    removePersistentItem('vrs_own_queue_request');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    resetConferenceLifecycleState();
    mobileLog('info', 'vrs_call_moved_elsewhere', { callId: data.callId }, { console: false });
    emitMobileTelemetryEvent('vrs_call_moved_elsewhere', {}, {
        callId: data.callId,
        roomName: data.roomName || undefined,
        state: 'moved'
    });
    store.dispatch(interpretedCallEnded('moved_to_other_device'));
    leaveConferenceWithoutEnding(store.dispatch);
    dismissAccountDeviceNotice('moving');
    presentAccountDeviceNotice(moveNotice('moved', data), moveBack ? {
        home: routeToRoleHome,
        moveBack: () => {
            if (saved) {
                setPersistentItem('vrs_active_call', JSON.stringify(saved));
            }
            moveBack();
        }
    } : {});
}
let heldSessionWatch;
/**
 * One live call per account across devices (vrs-server/lib/call-holders.js):
 * the notices this device shows when the server holds it out, supersedes it,
 * or moves the client's call to another of the account's devices.
 */
function installAccountDeviceNotices(store) {
    bindAccountDeviceDispatch(store.dispatch);
    // Nothing listened for this before, so a superseded room page just went
    // quiet (the demo incident). Say so, and offer to switch back.
    queueService.on('sessionSuperseded', data => {
        if (!isAccountDeviceRoomPage()) {
            return;
        }
        const { reload } = accountDevicePageActions();
        const sameBrowser = data.sameBrowser === true;
        presentAccountDeviceNotice({ kind: 'superseded', message: data.message, sameBrowser }, reload && !sameBrowser ? { useThisDevice: reload } : {});
    });
    queueService.on('sessionHeldElsewhere', hold => {
        heldSessionWatch?.stop();
        heldSessionWatch = undefined;
        if (!isAccountDeviceRoomPage()) {
            return;
        }
        heldSessionWatch = watchHeldSession({
            dismiss: dismissAccountDeviceNotice,
            goHome: routeToRoleHome,
            hold,
            present: presentAccountDeviceNotice,
            service: {
                off: (event, callback) => queueService.off(event, callback),
                on: (event, callback) => queueService.on(event, callback),
                requestAccountCallStatus: () => queueService.requestAccountCallStatus(),
                retakeSession: () => queueService.retakeSession()
            }
        });
    });
    queueService.on('authenticated', () => {
        heldSessionWatch?.stop();
        heldSessionWatch = undefined;
        dismissAccountDeviceNotice('held');
    });
    queueService.on('callMovingElsewhere', data => {
        if (callMoveBelongsHere(data)) {
            presentAccountDeviceNotice(moveNotice('moving', data), {});
        }
    });
    queueService.on('callMoveLapsed', data => {
        if (callMoveBelongsHere(data)) {
            presentAccountDeviceNotice(moveNotice('lapsed', data), {});
        }
    });
    queueService.on('callMovedElsewhere', data => leaveMovedCall(store, data));
    queueService.on('callEndIgnored', data => {
        mobileLog('info', 'vrs_call_end_ignored', { callId: data.callId, reason: data.reason }, { console: false });
    });
    queueService.on('callActiveElsewhere', data => {
        mobileLog('info', 'vrs_call_active_elsewhere', { callId: data.callId }, { console: false });
    });
}
function recordConferenceJoinFailure(action, event) {
    const activeCall = getStoredActiveCall();
    if (!activeCall || conferenceJoinedForActiveCall) {
        return;
    }
    conferenceJoinFailure = { event, ...conferenceErrorDetails(action.error) };
    mobileLog('error', 'vrs_conference_join_failed', { ...conferenceJoinFailure }, { console: false });
    emitMobileTelemetryEvent('vrs_conference_join_failed', { ...conferenceJoinFailure }, {
        callId: activeCall.callId,
        requestId: activeCall.requestId,
        roomName: activeCall.roomName,
        state: 'join_failed'
    });
}
function preserveMatchedCallBeforeJoin(reason) {
    const activeCall = getStoredActiveCall();
    const exitKey = getActiveCallExitKey(activeCall);
    if (!exitKey || preJoinExitHandledKey === exitKey) {
        return;
    }
    preJoinExitHandledKey = exitKey;
    emitMobileTelemetryEvent('vrs_prejoin_exit_preserved', {
        failure: conferenceJoinFailure,
        reason
    }, {
        callId: activeCall?.callId,
        requestId: activeCall?.requestId,
        roomName: activeCall?.roomName,
        state: 'retryable'
    });
}
/**
 * Whether the stored call is a group call (its own leave-and-rejoin rules).
 *
 * @param {StoredActiveCall} activeCall - The stored call.
 * @returns {boolean}
 */
function isGroupCallRecord(activeCall) {
    return activeCall.groupCall === true || Boolean(activeCall.roomName?.startsWith('group-'));
}
/**
 * The room's latest camera change: a camera track added, or a camera turned
 * back on (TRACK_UPDATED carrying muted: false). Screen shares do not count.
 *
 * @param {Object} action - A TRACK_ADDED or TRACK_UPDATED action.
 * @returns {void}
 */
function noteVideoChange(action) {
    const track = action.track;
    if (!track || track.videoType === 'desktop') {
        return;
    }
    const mediaType = track.mediaType ?? track.jitsiTrack?.getType?.();
    if (mediaType !== 'video') {
        return;
    }
    const side = (track.local ?? track.jitsiTrack?.isLocal?.()) === true ? 'local' : 'remote';
    if (action.type === TRACK_ADDED) {
        lastVideoChange = {
            at: Date.now(),
            kind: `${side}_camera_added`
        };
    }
    else if (track.muted === false) {
        lastVideoChange = {
            at: Date.now(),
            kind: `${side}_camera_on`
        };
    }
}
/**
 * The joined call's conference failed. A media-session failure (the set
 * jitsi-meet answers with "reload and rejoin") is recorded so the exit that
 * follows keeps the call; every in-call failure is reported, with the camera
 * change that preceded it.
 *
 * Runs BEFORE the reducer sees the error: the reload overlay (web) and dialog
 * (native) read the rejoin marker for their countdown as they mount.
 *
 * @param {Object} action - The CONFERENCE_FAILED action.
 * @returns {void}
 */
function noteInCallConferenceFailure(action) {
    const activeCall = getStoredActiveCall();
    const error = action.error;
    if (!activeCall?.callId || !activeCall.roomName || !conferenceJoinedForActiveCall
        || !action.conference || action.conference !== joinedConferenceForActiveCall
        || !isConferenceForActiveRoom(activeCall.roomName, action.conference)) {
        return;
    }
    const now = Date.now();
    const errorName = typeof error?.name === 'string' ? error.name.slice(0, 120) : 'unknown';
    const mediaSession = isMediaSessionFailure(errorName);
    const groupCall = isGroupCallRecord(activeCall);
    const marker = mediaSession && !groupCall
        ? recordMediaFailure(readMediaRejoinMarker(now), {
            callId: activeCall.callId,
            roomName: activeCall.roomName
        }, errorName, now)
        : undefined;
    if (marker) {
        writeMediaRejoinMarker(marker);
        inCallMediaFailure = {
            callId: activeCall.callId,
            conference: action.conference,
            rejoin: marker.pending
        };
    }
    let outcome = 'not_media_session';
    if (mediaSession) {
        outcome = groupCall ? 'group_call' : marker?.pending ? 'rejoin' : 'rejoin_limit_reached';
    }
    const details = {
        attempt: marker?.attempts,
        error: errorName,
        message: mediaFailureMessage(error?.params),
        msSinceJoin: activeConferenceJoinedAt === undefined ? undefined : Math.max(0, now - activeConferenceJoinedAt),
        msSinceVideoChange: lastVideoChange ? Math.max(0, now - lastVideoChange.at) : undefined,
        outcome,
        role: getUserRole(),
        videoChange: lastVideoChange?.kind
    };
    mobileLog('warn', 'vrs_in_call_conference_failed', details, { console: false });
    emitMobileTelemetryEvent('vrs_in_call_conference_failed', details, {
        callId: activeCall.callId,
        // The web page reloads seconds later: send now, not with the batch.
        keepalive: true,
        requestId: activeCall.requestId,
        roomName: activeCall.roomName,
        state: outcome
    });
}
/**
 * The failed conference is leaving. The call stays: no call_end, the stored
 * call is kept, and the page is not sent home. The reload overlay (web) or
 * dialog (native) of jitsi-meet rejoins the same room.
 *
 * @param {string} reason - The exit being kept.
 * @returns {void}
 */
function keepCallForMediaRejoin(reason) {
    const failure = inCallMediaFailure;
    if (!failure || failure.kept) {
        return;
    }
    failure.kept = true;
    const activeCall = getStoredActiveCall();
    emitMobileTelemetryEvent('vrs_in_call_rejoin_pending', {
        reason,
        reloadSeconds: MEDIA_REJOIN_RELOAD_SECONDS
    }, {
        callId: failure.callId,
        keepalive: true,
        requestId: activeCall?.requestId,
        roomName: activeCall?.roomName,
        state: 'rejoining'
    });
}
/**
 * Back in the call's room after a media-session failure: report how long the
 * rejoin took, and keep the attempt count for the budget.
 *
 * @param {StoredActiveCall} activeCall - The stored call.
 * @returns {void}
 */
function completeMediaRejoin(activeCall) {
    inCallMediaFailure = undefined;
    const now = Date.now();
    const marker = readMediaRejoinMarker(now);
    if (!marker?.pending || !activeCall?.callId || marker.callId !== activeCall.callId) {
        return;
    }
    writeMediaRejoinMarker({
        ...marker,
        pending: false
    });
    emitMobileTelemetryEvent('vrs_in_call_rejoin_succeeded', {
        attempt: marker.attempts,
        error: marker.error,
        rejoinMs: Math.max(0, now - marker.lastFailedAt)
    }, {
        callId: activeCall.callId,
        requestId: activeCall.requestId,
        roomName: activeCall.roomName,
        state: 'rejoined'
    });
}
/**
 * The call is over here: nothing is pending a rejoin any more.
 *
 * @returns {void}
 */
function forgetMediaRejoin() {
    inCallMediaFailure = undefined;
    if (readMediaRejoinMarker()) {
        writeMediaRejoinMarker(null);
    }
}
/**
 * Returns the matched queue room name when present.
 *
 * @param {Object} data - The queue match payload.
 * @returns {string|undefined}
 */
function getQueueRoomName(data) {
    return data.roomName?.trim()
        ? data.roomName
        : undefined;
}
/**
 * Returns a stable navigation key for a queue match event.
 *
 * @param {Object} data - The queue match payload.
 * @param {string} roomName - The matched room name.
 * @returns {string}
 */
function getQueueNavigationKey(data, roomName) {
    return data.callId || data.requestId || roomName;
}
function dispatchRoomNavigation(store, data, options) {
    rememberJoinStart(data, getCurrentBrowserRoomName() ? 'in_room_navigate' : 'auto_enter');
    const reportFailure = (error) => {
        const details = conferenceErrorDetails(error);
        mobileLog('error', 'queue_room_navigation_failed', details, { console: false });
        emitMobileTelemetryEvent('queue_room_navigation_failed', details, {
            callId: data.callId,
            requestId: data.requestId,
            roomName: data.roomName,
            state: 'join_failed'
        });
    };
    try {
        const result = store.dispatch(appNavigate(getRoomNavigationTarget(data.roomName || ''), options));
        const catchResult = result && typeof result.catch === 'function'
            ? result.catch(reportFailure)
            : undefined;
        void catchResult;
    }
    catch (error) {
        reportFailure(error);
    }
}
function getCurrentBrowserRoomName() {
    const pathname = typeof window !== 'undefined' ? window.location?.pathname : undefined;
    if (!pathname) {
        return undefined;
    }
    const parts = pathname.split('/').filter(Boolean);
    const roomName = parts[parts.length - 1];
    return roomName && !roomName.endsWith('.html') ? decodeURIComponent(roomName) : undefined;
}
function toMatchData(data) {
    const roomName = data.roomName || '';
    return {
        callerIdPhone: data.callerIdPhone,
        callId: data.callId || data.requestId || roomName,
        callType: data.callType,
        clientId: data.clientId || '',
        clientName: data.clientName || 'Client',
        clientStartWithVideoMuted: data.clientStartWithVideoMuted,
        interpreterId: data.interpreterId || '',
        interpreterName: data.interpreterName || 'Interpreter',
        language: data.language || 'ASL',
        requestKind: data.requestKind,
        requestId: data.requestId || data.callId || roomName,
        roomName,
        targetPhone: data.targetPhone,
        targetPhoneLast4: data.targetPhoneLast4,
        vcoEnabled: data.vcoEnabled
    };
}
/**
 * The client's own Zoom/Meet/Teams/Webex meeting named by a payload, if any
 * ("Interpret my meeting", vrs-server/lib/external-meeting-vrs.js).
 */
function toExternalMeeting(value) {
    return value?.platform ? { host: value.host, platform: value.platform, url: value.url } : undefined;
}
function toInterpreterRequest(data) {
    return {
        externalMeeting: toExternalMeeting(data.externalMeeting),
        callerIdPhone: data.callerIdPhone,
        callType: data.callType,
        clientName: data.clientName,
        corporateAccountId: data.corporateAccountId,
        costCenter: data.costCenter,
        department: data.department,
        id: data.id,
        industryTag: data.industryTag,
        instructions: data.instructions,
        language: data.language,
        offerId: data.offerId,
        offerExpiresAt: data.offerExpiresAt,
        requestKind: data.requestKind,
        roomName: data.roomName,
        scheduledSessionId: data.scheduledSessionId,
        scheduledStartAt: data.scheduledStartAt,
        targetPhone: data.targetPhone,
        targetPhoneLast4: data.targetPhoneLast4,
        timestamp: data.timestamp || Date.now(),
        vcoEnabled: data.vcoEnabled,
        demoMode: data.demoMode === true
    };
}
function applyProfileDisplayName(store) {
    // Without this the other side's name pill falls back to the role word
    // ("Client") — carry the signed-in profile name into the conference.
    const user = getPersistentJson('vrs_user_info');
    const name = (user?.name || '').trim();
    if (name) {
        store.dispatch(updateSettings({ displayName: name }));
    }
}
function shouldStartClientVideoMuted(store, data) {
    if (typeof data?.clientStartWithVideoMuted === 'boolean') {
        return data.clientStartWithVideoMuted;
    }
    const serverPreference = store.getState()?.['features/call-management']?.preferences?.camera_default_off;
    if (typeof serverPreference === 'boolean') {
        return serverPreference;
    }
    return getClientStartWithVideoMuted();
}
/**
 * Matches are delivered to every signed-in device of the account (so pushes
 * can wake sleeping phones). Only the device that placed the request — or is
 * already on this very call — may silently auto-enter. Any other device
 * rings instead: without this, a logged-in phone and laptop take turns
 * superseding each other's room session and the camera strobes on/off.
 */
function isMatchForThisDevice(data) {
    const stored = getStoredActiveCall();
    const own = getPersistentJson('vrs_own_queue_request');
    // Interpreters bypass the own-request marker: the server confirms an
    // accept only to the socket that sent it, so an interpreter match is
    // always this device's accept — gating it here silently swallowed the
    // terp accept→room navigation. Clients keep the f22ef958 rule.
    return shouldAutoEnterMatchedRoom({
        matchCallId: data.callId,
        ownRequestAt: own?.at,
        role: getUserRole(),
        storedCallId: stored?.callId
    });
}
function autoEnterQueueRoom(store, data, options) {
    const roomName = getQueueRoomName(data);
    if (!roomName) {
        return;
    }
    if (!isMatchForThisDevice(data)) {
        emitMobileTelemetryEvent('queue_match_other_device', {}, {
            callId: data.callId,
            requestId: data.requestId,
            roomName,
            state: 'ringing'
        });
        // Web gets the deaf-accessible ring overlay; native devices are rung
        // by the CallKit/FCM push for this same match.
        const isNative = typeof navigator !== 'undefined'
            && navigator.product === 'ReactNative';
        if (!isNative && data.callId) {
            store.dispatch(incomingP2PCall({
                callId: data.callId,
                roomName,
                callerId: '',
                // Named by the same rule the CallKit/Telecom ring uses, so
                // the same match is not one thing on the phone and another in
                // the browser. Never the interpreter's name.
                callerName: getCallPeerLabel(data, getUserRole()) || 'Your interpreter is ready',
                clientStartWithVideoMuted: data.clientStartWithVideoMuted,
                hasVideo: true,
                groupCall: false,
                inboundVrs: true
            }));
        }
        return;
    }
    applyProfileDisplayName(store);
    const navigationKey = getQueueNavigationKey(data, roomName);
    if (lastAutoEnteredQueueRoom === navigationKey) {
        return;
    }
    lastAutoEnteredQueueRoom = navigationKey;
    // Persist match metadata for CDR/call history on call end
    persistMatchData(data);
    startActiveCallReconciliationLoop(store);
    if (getCurrentBrowserRoomName() === roomName) {
        return;
    }
    queueRoomNavigationStartedAt = Date.now();
    queueRoomSignalingConnectedAt = undefined;
    queueRoomMediaConnectedAt = undefined;
    queueRoomRemoteVideoAt = undefined;
    emitMobileTelemetryEvent('queue_room_auto_enter', {
        startWithAudioMuted: options.startWithAudioMuted,
        startWithVideoMuted: options.startWithVideoMuted
    }, {
        callId: data.callId,
        requestId: data.requestId,
        roomName,
        state: 'joining'
    });
    // Queue joins bypass prejoin/lobby screens; media defaults differ by role.
    // Navigate to an absolute tenant URL on native so the self-hosted bridge
    // is used instead of the public Jitsi (meet.jit.si) default.
    dispatchRoomNavigation(store, data, {
        hidePrejoin: true,
        startWithAudioMuted: options.startWithAudioMuted,
        startWithVideoMuted: options.startWithVideoMuted
    });
}
function enterPlatformCallRoom(store, data, peerName) {
    if (!data.roomName) {
        return;
    }
    // p2p_join_room / p2p_ringing can be replayed (reconnect delivery, a
    // sibling instance re-publishing). Same call, same room, already there:
    // nothing to do — the queue path has had this guard for a while.
    if (lastPlatformCall
        && lastPlatformCall.roomName === data.roomName
        && (lastPlatformCall.callId || '') === (data.callId || '')
        && getCurrentBrowserRoomName() === data.roomName) {
        return;
    }
    lastPlatformCall = { callId: data.callId, roomName: data.roomName };
    // A reloaded group room asked to be seated again (groupRejoin.ts): this
    // p2p_join_room is the answer, and the page is already on its way in.
    const rejoiningHere = isGroupRejoinPending(data.callId) && getCurrentBrowserRoomName() === data.roomName;
    applyProfileDisplayName(store);
    persistMatchData({
        callId: data.callId,
        callType: data.callType,
        // Who placed the call (calls.client_id): only they are offered the
        // room's interpreter button (p2pInterpreterEligibility) and, in a
        // group call, ending it for everyone (groupEndForAll.ts).
        clientId: data.clientId,
        groupCall: data.groupCall,
        interpreterId: data.interpreterId,
        interpreterName: data.interpreterName,
        clientStartWithVideoMuted: data.clientStartWithVideoMuted,
        roomName: data.roomName,
        clientName: [peerName, data.calleeName, data.callerName].map(usablePlatformPeerName).find(Boolean)
            || (data.groupCall ? 'Group call' : 'Direct call'),
        language: 'ASL'
    });
    startActiveCallReconciliationLoop(store);
    emitMobileTelemetryEvent('platform_call_room_enter', {
        groupCall: Boolean(data.groupCall),
        participantCount: data.participantCount
    }, {
        callId: data.callId,
        requestId: data.requestId,
        roomName: data.roomName,
        state: 'joining'
    });
    if (rejoiningHere) {
        return;
    }
    dispatchRoomNavigation(store, data, {
        hidePrejoin: true,
        // Starting or accepting the platform call is the consent boundary;
        // no media is acquired while Home or the incoming sheet is idle.
        startWithAudioMuted: false,
        startWithVideoMuted: data.inboundVrs === true
            ? shouldStartClientVideoMuted(store, data)
            : false
    });
}
function hasInterpreterSessionToken() {
    return Boolean(getSecureItem('vrs_auth_token'));
}
function getDesktopBridge() {
    return typeof window !== 'undefined'
        ? window.MalkaDesktop
        : undefined;
}
function notifyDesktopOfQueue(action, getState) {
    const bridge = getDesktopBridge();
    if (!bridge) {
        return;
    }
    if (action.type === INTERPRETER_REQUEST_RECEIVED && action.request) {
        const request = action.request;
        bridge.incomingCall({
            callType: request.callType || 'vrs',
            clientName: request.clientName || 'Client',
            language: request.language || 'ASL',
            requestKind: request.requestKind || 'standard',
            targetPhone: request.targetPhone
        });
        return;
    }
    // Any resolution — accepted, declined, matched — clears the alert once
    // no requests remain pending.
    if (action.type === REQUEST_ACCEPTED || action.type === REQUEST_DECLINED || action.type === MATCH_FOUND) {
        const pending = getState()?.['features/interpreter-queue']?.pendingRequests || [];
        if (action.type === MATCH_FOUND || pending.length === 0) {
            bridge.clearIncomingCall();
        }
    }
}
// Module-scoped rather than a store property: sign-out paths must be able to
// reset it without reaching into the store (see resetInterpreterQueueMiddleware).
let _interpreterQueueMiddlewareInitialized = false;
// The store the listeners were wired to, so sign-out can clear what they set.
let interpreterQueueStore;
// lib-jitsi-meet's JitsiConferenceErrors.CONFERENCE_MAX_USERS. The value, not
// an import: this middleware must not load the SDK (web-join-timeline guard).
const CONFERENCE_MAX_USERS_ERROR = 'conference.max_users';
// The call whose "media is up here" signal this page already sent.
let movedHereSignalledFor;
MiddlewareRegistry.register((store) => (next) => (action) => {
    // Before the reducer: see noteInCallConferenceFailure.
    if (action.type === CONFERENCE_FAILED && hasStoredActiveCall()) {
        noteInCallConferenceFailure(action);
    }
    const result = next(action);
    notifyDesktopOfQueue(action, store.getState);
    if ((action.type === CONFERENCE_FAILED || action.type === CONNECTION_FAILED) && hasStoredActiveCall()) {
        recordConferenceJoinFailure({ error: action.error }, action.type);
    }
    if (action.type === CONNECTION_ESTABLISHED
        || action.type === DATA_CHANNEL_OPENED
        || action.type === TRACK_ADDED) {
        ensureJoinStartRestored();
    }
    if (action.type === CONNECTION_ESTABLISHED
        && hasStoredActiveCall()
        && queueRoomNavigationStartedAt
        && !queueRoomSignalingConnectedAt) {
        const activeCall = getStageEventCall();
        queueRoomSignalingConnectedAt = Date.now();
        emitMobileTelemetryEvent('vrs_signaling_connected', {
            elapsedMs: Math.max(0, queueRoomSignalingConnectedAt - queueRoomNavigationStartedAt),
            sinceBootMs: sinceDocumentStartMs(),
            role: getUserRole(),
            source: queueRoomJoinSource
        }, {
            callId: activeCall?.callId,
            requestId: activeCall?.requestId,
            roomName: activeCall?.roomName,
            state: 'connected'
        });
    }
    // This device's media connection to the bridge is open. If the
    // client's call was moving here, the server may now release the other
    // device: the interpreter's client pane moves here the moment it
    // leaves, so it must not happen before this device can send video.
    // (Joining the conference is too early: media can take seconds more.)
    if (action.type === DATA_CHANNEL_OPENED && hasStoredActiveCall() && getUserRole() === 'client') {
        const activeCall = getStoredActiveCall();
        if (activeCall?.callId && movedHereSignalledFor !== activeCall.callId) {
            movedHereSignalledFor = activeCall.callId;
            queueService.notifyCallRoomJoined(activeCall.callId);
        }
    }
    // A full room refuses this device. If the client's call was moving
    // here it stays on the other device, which the server tells why.
    if (action.type === CONFERENCE_FAILED && hasStoredActiveCall() && getUserRole() === 'client'
        && action.error?.name === CONFERENCE_MAX_USERS_ERROR) {
        const activeCall = getStoredActiveCall();
        if (activeCall?.callId) {
            queueService.notifyCallMoveFailed(activeCall.callId, 'room_full');
        }
    }
    if (action.type === DATA_CHANNEL_OPENED
        && hasStoredActiveCall()
        && queueRoomNavigationStartedAt
        && !queueRoomMediaConnectedAt) {
        const activeCall = getStageEventCall();
        queueRoomMediaConnectedAt = Date.now();
        emitMobileTelemetryEvent('vrs_media_connected', {
            elapsedMs: Math.max(0, queueRoomMediaConnectedAt - queueRoomNavigationStartedAt),
            signalingToMediaMs: queueRoomSignalingConnectedAt
                ? Math.max(0, queueRoomMediaConnectedAt - queueRoomSignalingConnectedAt)
                : undefined,
            sinceBootMs: sinceDocumentStartMs(),
            role: getUserRole(),
            source: queueRoomJoinSource
        }, {
            callId: activeCall?.callId,
            requestId: activeCall?.requestId,
            roomName: activeCall?.roomName,
            state: 'media_connected'
        });
    }
    if (action.type === TRACK_ADDED || action.type === TRACK_UPDATED) {
        noteVideoChange(action);
    }
    const addedTrack = action.type === TRACK_ADDED
        ? action.track
        : undefined;
    if (addedTrack
        && !addedTrack.local
        && addedTrack.mediaType === 'video'
        && hasStoredActiveCall()
        && queueRoomNavigationStartedAt
        && !queueRoomRemoteVideoAt) {
        const activeCall = getStageEventCall();
        queueRoomRemoteVideoAt = Date.now();
        emitMobileTelemetryEvent('vrs_remote_video_received', {
            elapsedMs: Math.max(0, queueRoomRemoteVideoAt - queueRoomNavigationStartedAt),
            mediaToVideoMs: queueRoomMediaConnectedAt
                ? Math.max(0, queueRoomRemoteVideoAt - queueRoomMediaConnectedAt)
                : undefined,
            sinceBootMs: sinceDocumentStartMs(),
            role: getUserRole(),
            source: queueRoomJoinSource
        }, {
            callId: activeCall?.callId,
            requestId: activeCall?.requestId,
            roomName: activeCall?.roomName,
            state: 'remote_video'
        });
        // The timeline is complete; a stale marker must not be adopted by
        // the next room this tab opens.
        try {
            removePersistentItem(JOIN_TIMELINE_KEY);
        }
        catch { /* storage blocked */ }
    }
    if (action.type === CONFERENCE_JOINED && hasStoredActiveCall()) {
        const activeCall = getStoredActiveCall();
        if (isConferenceForActiveRoom(activeCall?.roomName, action.conference)) {
            conferenceJoinedForActiveCall = true;
            joinedConferenceForActiveCall = action.conference;
            conferenceJoinFailure = undefined;
            preJoinExitHandledKey = undefined;
            const joinedAt = Date.now();
            activeConferenceJoinedAt = joinedAt;
            completeMediaRejoin(activeCall);
            if (action.conference) {
                observeMediaTransport(action.conference, {
                    callId: activeCall?.callId,
                    requestId: activeCall?.requestId,
                    roomName: activeCall?.roomName
                });
            }
            emitMobileTelemetryEvent('vrs_conference_joined', {
                elapsedMs: queueRoomNavigationStartedAt
                    ? Math.max(0, joinedAt - queueRoomNavigationStartedAt)
                    : undefined,
                signalingToConferenceMs: queueRoomSignalingConnectedAt
                    ? Math.max(0, joinedAt - queueRoomSignalingConnectedAt)
                    : undefined
            }, {
                callId: activeCall?.callId,
                requestId: activeCall?.requestId,
                roomName: activeCall?.roomName,
                state: 'joined'
            });
        }
    }
    if (action.type === CONFERENCE_WILL_LEAVE && hasStoredActiveCall()) {
        const activeCall = getStoredActiveCall();
        const mediaFailure = inCallMediaFailure && inCallMediaFailure.conference === action.conference
            ? inCallMediaFailure : undefined;
        // On web, conference.js leaves a room whose media session failed
        // by itself. That is not this person hanging up: the reload
        // overlay rejoins the call (inCallMediaRejoin.ts). Past the rejoin budget
        // the call ends as before.
        if (mediaFailure?.rejoin) {
            keepCallForMediaRejoin('conference_will_leave');
        }
        else if (mediaFailure) {
            finalizeConferenceExit(store, 'media_rejoin_limit_reached');
        }
        else if (shouldPreserveMatchedCallAfterExit(conferenceJoinedForActiveCall, activeCall?.roomName, joinedConferenceForActiveCall, action.conference)) {
            preserveMatchedCallBeforeJoin('conference_will_leave');
        }
        else {
            finalizeConferenceExit(store, 'conference_will_leave');
        }
    }
    const transportObserver = mediaTransportObserver;
    if (action.type === CONFERENCE_LEFT && transportObserver && transportObserver.conference === action.conference) {
        transportObserver.dispose();
    }
    if (action.type === CONFERENCE_LEFT && hasStoredActiveCall()) {
        const activeCall = getStoredActiveCall();
        if (inCallMediaFailure?.rejoin && inCallMediaFailure.conference === action.conference) {
            // Native: the SDK left the failed room; PageReloadDialog rejoins.
            keepCallForMediaRejoin('conference_left');
        }
        else if (shouldPreserveMatchedCallAfterExit(conferenceJoinedForActiveCall, activeCall?.roomName, joinedConferenceForActiveCall, action.conference, false)) {
            preserveMatchedCallBeforeJoin('conference_left');
        }
        else {
            finalizeConferenceExit(store, 'conference_left');
        }
    }
    // Native: leaving from the reload dialog (Cancel, or hanging up)
    // instead of rejoining. The call was kept only for the rejoin, so
    // leaving ends it; otherwise nobody would, and the other person would
    // wait in the room.
    if (action.type === SET_ROOM && !action.room && inCallMediaFailure && hasStoredActiveCall()) {
        finalizeConferenceExit(store, 'media_rejoin_cancelled');
    }
    if (action.type === APP_STATE_CHANGED && action.appState === 'active') {
        startActiveCallReconciliationLoop(store);
        reconcileLocalActiveCall(store, 'app_foreground_reconcile');
        if (getUserRole() === 'interpreter'
            && queueService.getLastInterpreterStatus() === 'active'
            && !getStoredActiveCall()?.roomName) {
            queueService.reconnect();
        }
    }
    // Initialize queue service listeners on first action
    if (!_interpreterQueueMiddlewareInitialized) {
        const initialized = initializeInterpreterQueue(store);
        if (initialized) {
            _interpreterQueueMiddlewareInitialized = true;
        }
    }
    return result;
});
/**
 * Sign-out calls queueService.disconnect(), which clears every service
 * listener. Without this reset the middleware never re-subscribes, so offers
 * and matches after a re-login reach zero listeners.
 */
export function resetInterpreterQueueMiddleware() {
    _interpreterQueueMiddlewareInitialized = false;
    // Signed out: not Available (keep-awake off), no post-call card, and no
    // waiting count left for the next account's breather.
    interpreterQueueStore?.dispatch(interpreterAvailabilityChanged(false));
    interpreterQueueStore?.dispatch(dismissInterpreterWrapUp());
    forgetWrapUpQueueCount();
    interpreterQueueStore = undefined;
}
/**
 * Initializes the interpreter queue by setting up event listeners
 * on the queue service and connecting them to Redux actions.
 *
 * @param {Object} store - The Redux store.
 */
function initializeInterpreterQueue(store) {
    const userRole = getUserRole();
    console.log('[InterpreterQueue] Initializing for role:', userRole);
    if (userRole === 'interpreter' && !hasInterpreterSessionToken()) {
        console.log('[InterpreterQueue] Waiting for interpreter authentication before connecting');
        return false;
    }
    startActiveCallReconciliationLoop(store);
    interpreterQueueStore = store;
    installAccountDeviceNotices(store);
    // Connection status events - all users
    queueService.on('connection', (data) => {
        store.dispatch(queueConnectionChanged(data.connected));
        // The socket's waiting count went with it (a drop, or superseded).
        if (!data.connected && userRole === 'interpreter') {
            forgetWrapUpQueueCount(store);
        }
    });
    queueService.on('notificationEvent', (data) => {
        if (!data.body || typeof data.type !== 'string' || !['admin_announcement', 'manager_message', 'manager_observer_presence'].includes(data.type)) {
            return;
        }
        const sentAt = typeof data.data?.sentAt === 'string' ? data.data.sentAt : String(Date.now());
        mobileLog('info', `queue_${data.type}_received`, {}, { console: false });
        store.dispatch(showNotification({
            titleKey: undefined,
            title: data.title || (data.type === 'manager_message' ? 'Manager note' : 'Queue announcement'),
            descriptionKey: undefined,
            description: data.body,
            uid: `${data.type}-${sentAt}`
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    });
    queueService.on('conferenceInvitationError', data => {
        // Invitation-specific answer failures are shown by the incoming-call surfaces.
        if (data.invitationId)
            return;
        store.dispatch(showNotification({
            titleKey: 'incomingCall.invitationDeliveryFailed',
            descriptionKey: data.message ? undefined : 'incomingCall.invitationDeliveryRetry',
            description: data.message,
            uid: `conference-invitation-error-${data.callId || 'refresh'}`
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    });
    // Client-specific events
    if (userRole === 'client') {
        // Client receives notification when their request is queued
        queueService.on('requestQueued', (data) => {
            console.log('[InterpreterQueue] Request queued, position:', data.position);
            store.dispatch(queueStatusUpdate({
                requestId: data.requestId,
                queuePosition: data.position
            }));
        });
        // An interpreter for the client's own meeting: there is no room to
        // enter, on this device or any other of the account's devices.
        const externalMeetingMatched = (data) => {
            const meeting = toExternalMeeting(data.externalMeeting);
            if (!meeting || !data.callId)
                return false;
            const current = store.getState()['features/interpreter-queue']?.externalMeetingCall;
            if (current?.callId === data.callId)
                return true;
            store.dispatch({
                type: EXTERNAL_MEETING_CALL_UPDATED,
                externalMeetingCall: {
                    callId: data.callId,
                    interpreterName: data.interpreterName || 'Your interpreter',
                    platform: meeting.platform,
                    roomName: data.roomName,
                    stage: 'joining'
                }
            });
            mobileLog('info', 'queue_external_meeting_matched', { platform: meeting.platform }, { console: false });
            return true;
        };
        // Calendar meetings that are starting (phase 2): the VRS Home card.
        queueService.on('meetingReminder', (data) => {
            const reminders = (Array.isArray(data.reminders) ? data.reminders : []).flatMap(item => {
                const parsed = meetingReminderSchema.safeParse(item);
                return parsed.success ? [parsed.data] : [];
            });
            if (reminders.length)
                store.dispatch({ type: MEETING_REMINDERS_ADDED, reminders });
        });
        queueService.on('externalMeetingProgress', (data) => {
            const current = store.getState()['features/interpreter-queue']?.externalMeetingCall;
            if (!current || current.callId !== data.callId || !data.event)
                return;
            store.dispatch({ type: EXTERNAL_MEETING_CALL_UPDATED, externalMeetingCall: { ...current, stage: data.event } });
        });
        // Client receives notification when a match is found
        queueService.on('matchFound', (data) => {
            console.log('[InterpreterQueue] Match found:', data);
            if (externalMeetingMatched(data))
                return;
            store.dispatch(matchFound(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: shouldStartClientVideoMuted(store, data)
            });
        });
        // Client receives notification when meeting is initiated
        queueService.on('meetingInitiated', (data) => {
            console.log('[InterpreterQueue] Meeting initiated:', data);
            if (externalMeetingMatched(data))
                return;
            store.dispatch(meetingInitiated(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: shouldStartClientVideoMuted(store, data)
            });
        });
        // P2P call: ringing → auto-enter the room
        queueService.on('p2pRinging', (data) => {
            console.log('[InterpreterQueue] P2P ringing:', data);
            handleGroupRinging(store, data);
            enterPlatformCallRoom(store, data, data.calleeName);
        });
        // A hearing party dialed this client's VRS number: drive the same
        // deaf-accessible incoming-call overlay with the caller's number.
        // Answering joins the room the interpreter request was queued into.
        queueService.on('inboundVrsCall', (payload) => {
            if (!isRecord(payload) || !stringValue(payload.roomName)) {
                mobileLog('warn', 'inbound_vrs_call_invalid', {}, { console: false });
                return;
            }
            const data = {
                callerName: stringValue(payload.callerName),
                clientStartWithVideoMuted: typeof payload.clientStartWithVideoMuted === 'boolean'
                    ? payload.clientStartWithVideoMuted : undefined,
                fromNumber: stringValue(payload.fromNumber),
                requestId: stringValue(payload.requestId),
                roomName: stringValue(payload.roomName)
            };
            mobileLog('info', 'inbound_vrs_call_received', {}, { console: false });
            // Native devices are rung by the CallKit/FCM push for this same
            // event; the overlay is the web surface.
            const isNativeApp = typeof navigator !== 'undefined'
                && navigator.product === 'ReactNative';
            if (!isNativeApp && data.roomName) {
                store.dispatch(incomingP2PCall({
                    callId: data.requestId || data.roomName,
                    roomName: data.roomName,
                    callerId: data.fromNumber || '',
                    callerName: data.callerName
                        || getPhoneCallLabel('vrs', data.fromNumber)
                        || 'Incoming VRS call',
                    clientStartWithVideoMuted: data.clientStartWithVideoMuted,
                    hasVideo: true,
                    groupCall: false,
                    inboundVrs: true
                }));
            }
        });
        queueService.on('p2pIncomingCall', (data) => {
            console.log('[InterpreterQueue] P2P incoming call:', data);
            rememberPlatformCallPeer(data.callId, data.callerId, data.callerName);
            // Native has its own CallKit + IncomingPlatformCallSheet flow
            // (subscribed directly to this event); on web nothing surfaced the
            // call at all. Drive the deaf-accessible incoming-call overlay.
            const isNative = typeof navigator !== 'undefined'
                && navigator.product === 'ReactNative';
            if (!isNative && data.callId && data.roomName) {
                store.dispatch(incomingP2PCall({
                    callId: data.callId,
                    roomName: data.roomName,
                    callerId: data.callerId || '',
                    callerName: data.callerName || `${getAppName()} contact`,
                    clientStartWithVideoMuted: data.clientStartWithVideoMuted,
                    hasVideo: data.hasVideo !== false,
                    conferenceInvitation: data.conferenceInvitation,
                    invitationId: data.invitationId,
                    expiresAt: data.expiresAt,
                    groupCall: Boolean(data.groupCall)
                }));
            }
        });
        queueService.on('p2pJoinRoom', (data) => {
            console.log('[InterpreterQueue] P2P join room:', data);
            // The answering side: the name the ring carried wins. Native sends
            // p2p_accept without a callerName, and a placeholder the answer
            // echoes back must not overwrite the ring's real name.
            const callerName = platformCallPeerName(data.callId, data.callerId, data.callerName);
            rememberPlatformCallPeer(data.callId, data.callerId, callerName);
            enterPlatformCallRoom(store, data, callerName);
        });
        queueService.on('p2pAccepted', (data) => {
            console.log('[InterpreterQueue] P2P accepted:', data);
            rememberPlatformCallPeer(data.callId, data.calleeId, data.calleeName);
            if (data.groupCall === true) {
                markPlatformCallGroup(data.callId);
            }
        });
        // Group calls: who came and went, briefly, for everyone in the room.
        queueService.on('p2pParticipantJoined', data => showPlatformParticipantNotice(store, 'joined', data));
        queueService.on('p2pParticipantLeft', data => showPlatformParticipantNotice(store, 'left', data));
        // Someone joining makes it a group: its host may end it for everyone.
        queueService.on('p2pParticipantJoined', data => markPlatformCallGroup(data.callId));
        // An interpreter took the caller's in-call request: record them on the
        // stored call so the room's interpreter button (web and native) stops
        // offering another, including after a reload.
        queueService.on('p2pInterpreterJoined', (data) => {
            recordP2PInterpreterOnCall(data?.callId, data?.interpreterName);
        });
        // The caller auto-enters the room on ringing, so a decline arriving
        // after that must be surfaced IN the room — otherwise they wait in an
        // empty room with no feedback.
        queueService.on('p2pDeclined', (data) => {
            console.log('[InterpreterQueue] P2P declined:', data);
            showP2PDeclinedNotice(store, data);
        });
        queueService.on('p2pGroupNoAnswer', data => handleP2PGroupNoAnswer(store, data));
        // P2P call: target offline / DND / failed — clear any web incoming
        // overlay and log.
        queueService.on('p2pCallFailed', (data) => {
            console.warn('[InterpreterQueue] P2P call failed:', data.message);
            store.dispatch(clearIncomingP2PCall());
        });
        // No answer / unreachable: the caller auto-entered the room on
        // ringing, so tell them there and take them back out. Mobile and the
        // home page already present deliveryReason; the web room did not.
        queueService.on('p2pTargetOffline', data => {
            const copy = describeP2PUnavailable(data, getAppName());
            console.warn('[InterpreterQueue] P2P target offline:', data.calleeName, data.deliveryReason);
            store.dispatch(showNotification({
                titleKey: undefined,
                title: copy.title,
                descriptionKey: undefined,
                description: copy.detail || (data.voicemailAvailable === true
                    ? 'You can leave a VideoMail message from Home.'
                    : 'You can try again later.'),
                uid: 'p2p-target-offline'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            const parked = lastPlatformCall
                && (lastPlatformCall.callId || '') === (String(data.callId || ''))
                && getCurrentBrowserRoomName() === lastPlatformCall.roomName;
            if (parked) {
                lastPlatformCall = undefined;
                store.dispatch(hangup(true));
            }
        });
        queueService.on('p2pTargetDnd', data => {
            console.warn('[InterpreterQueue] P2P target DND:', data.calleeName);
        });
    }
    // Interpreter-specific events
    if (userRole === 'interpreter') {
        // The server's confirmation, not the local wish: Malka Terp keeps the
        // screen awake only while it is really in the queue.
        queueService.on('statusUpdated', (data) => {
            const status = typeof data?.status === 'string' ? data.status.toLowerCase() : '';
            store.dispatch(interpreterAvailabilityChanged(status === 'active' || status === 'available'));
        });
        // Interpreter receives incoming interpreter requests
        queueService.on('interpreterRequest', (data) => {
            console.log('[InterpreterQueue] New interpreter request:', data);
            store.dispatch(interpreterRequestReceived(toInterpreterRequest(data)));
        });
        // Interpreter receives notification when request is assigned
        queueService.on('requestAssigned', (data) => {
            console.log('[InterpreterQueue] Request assigned:', data);
            // Meeting calls run from the desktop console (see requestAccepted).
            if (toExternalMeeting(data.externalMeeting))
                return;
            store.dispatch(meetingInitiated(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: false
            });
        });
    }
    // Common events for both roles
    // Summaries update counts and the client's own position. Interpreter
    // cards belong exclusively to targeted offers and their lifecycle events.
    queueService.on('queueStatus', (data) => {
        const currentRequestId = store.getState()['features/interpreter-queue']?.currentRequestId;
        const ownRequest = userRole === 'client' && currentRequestId
            ? data.pendingRequests.find(request => request.id === currentRequestId)
            : undefined;
        const position = ownRequest?.position;
        store.dispatch(queueStatusUpdate({
            activeInterpreters: data.activeInterpreters?.length || 0,
            queuePosition: typeof position === 'number' && Number.isInteger(position) && position > 0
                ? position : undefined,
            queueSize: typeof data.queueSize === 'number' ? data.queueSize : undefined
        }));
        // A waiting client this interpreter could take ends their post-call
        // breather early (the server's count; queueSize counts every request).
        if (userRole === 'interpreter') {
            noteWrapUpQueueStatus(store, data.eligibleQueueSize);
        }
    });
    // A request left the queue: the client cancelled, it timed out, or the
    // server withdrew it because another interpreter took it. Registered for
    // BOTH roles — this used to live in the client-only block, so on the terp
    // app `request_withdrawn` never cleared the incoming card and interpreters
    // were left an Accept button for a call that no longer existed.
    queueService.on('requestCancelled', (data) => {
        console.log('[InterpreterQueue] Request cancelled');
        const timedOut = data.timedOut === true || data.reason === 'timeout';
        if (getUserRole() === 'interpreter' && data.requestId) {
            const hadOffer = store.getState()['features/interpreter-queue']?.pendingRequests
                ?.some((request) => request.id === data.requestId);
            store.dispatch({ type: REQUEST_DECLINED, requestId: data.requestId });
            if (data.reason === 'offer_expired' && hadOffer) {
                store.dispatch(showNotification({
                    titleKey: undefined,
                    title: 'Interpreter offer expired',
                    descriptionKey: undefined,
                    description: 'This request is no longer available to you.',
                    uid: `queue-offer-expired-${data.requestId}`
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            // An admin paused the queue: the offer comes back when it resumes,
            // so this must not read as a decline or an expiry.
            if (data.reason === 'queue_paused' && hadOffer) {
                store.dispatch(showNotification({
                    titleKey: undefined,
                    title: 'Queue paused',
                    descriptionKey: undefined,
                    description: 'The queue was paused; this request will be offered again when it resumes.',
                    uid: `queue-offer-paused-${data.requestId}`
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
        }
        else {
            // Say why, as web does, but only when a request was waiting here:
            // the Cancel button clears first, so its own reply stays quiet.
            // Native toasts render only inside the conference, so the screens
            // show requestNotice themselves.
            const wasWaiting = Boolean(store.getState()['features/interpreter-queue']?.isRequestPending);
            const reason = timedOut
                ? 'No interpreter was available. Please try again.'
                : 'Your interpreter request was cancelled.';
            store.dispatch({
                type: CANCEL_REQUEST,
                notice: wasWaiting ? reason : undefined
            });
            removePersistentItem('vrs_own_queue_request');
        }
        if (timedOut) {
            emitMobileTelemetryEvent('queue_request_timed_out', {}, {
                requestId: data.requestId,
                state: 'timed_out'
            });
        }
    });
    // An admin kicked this account. InterpreterQueueService already stops
    // reconnecting and clears the stored credentials, but on native that alone
    // leaves the app sitting on VRS/Interpreter Home looking signed in until it
    // is force-quit — which is exactly how the kick read on device 2026-08-02.
    // The web surfaces do their own reload in client-profile.js and
    // interpreter-profile.html, so only native needs routing here.
    // Two ways the same thing arrives. The live one is admin_force_logout, sent
    // while the socket is open. The delayed one is a SESSION_REVOKED auth_error
    // on the next reconnect, which is what a kicked device sees when it was
    // backgrounded, offline, or on an older build when the kick landed — that
    // was the case in the 2026-08-03 device report, where the app stayed on Home
    // and merely printed "Session has been revoked" under Request Interpreter.
    const handleForcedSignOut = (event, defaultTitle) => (data) => {
        console.warn(`[InterpreterQueue] Forced sign-out (${event}):`, data);
        mobileLog('warn', 'interpreter_queue_forced_signout', {
            event,
            reason: typeof data?.reason === 'string' ? data.reason : undefined
        }, { console: false });
        // The web surfaces reload themselves in client-profile.js and
        // interpreter-profile.html; running this there would double-handle it.
        if (!isNativeRuntime()) {
            return;
        }
        // Leave any live conference first; resetRoot alone can leave the
        // Jitsi room mounted underneath on Android.
        try {
            store.dispatch(hangup(true));
        }
        catch {
            mobileLog('warn', 'interpreter_queue_forced_signout_hangup_failed', { event }, { console: false });
        }
        store.dispatch(showNotification({
            titleKey: undefined,
            title: defaultTitle,
            descriptionKey: undefined,
            description: typeof data?.reason === 'string' && data.reason
                ? data.reason
                : 'Please sign in again.',
            uid: 'forced-sign-out'
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        signOutMobileUser();
    };
    queueService.on('adminForceLogout', handleForcedSignOut('adminForceLogout', 'Signed out by an administrator'));
    queueService.on('sessionRevoked', handleForcedSignOut('sessionRevoked', 'Your session has ended'));
    queueService.on('requestAccepted', (data) => {
        console.log('[InterpreterQueue] Request accepted:', data);
        if (getUserRole() === 'interpreter') {
            if (data.requestId) {
                store.dispatch({ type: REQUEST_ACCEPTED, requestId: data.requestId });
            }
            // Meeting calls are run from the desktop console: the Terp app
            // loses its socket behind the Zoom app, and the stale-call sweep
            // would end the call. Never open a Malka room for one.
            if (toExternalMeeting(data.externalMeeting)) {
                store.dispatch(showNotification({
                    titleKey: 'Open the desktop console',
                    descriptionKey: 'This is a call in the client’s own meeting. Run it from the web console.',
                    uid: `external-meeting-${data.callId || data.requestId || 'call'}`
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                return;
            }
            store.dispatch(meetingInitiated(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: false
            });
        }
    });
    queueService.on('requestDeclined', (data) => {
        console.log('[InterpreterQueue] Request declined:', data);
        if (getUserRole() === 'interpreter' && data.requestId) {
            store.dispatch({ type: REQUEST_DECLINED, requestId: data.requestId });
        }
    });
    queueService.on('authenticated', (data) => {
        store.dispatch({ type: QUEUE_CAPABILITIES_UPDATED, capabilities: data.capabilities || {} });
    });
    queueService.on('callEnded', (data) => {
        console.log('[InterpreterQueue] Interpreted call ended:', data);
        if (data.callId)
            store.dispatch({ type: EXTERNAL_MEETING_CALL_ENDED, callId: data.callId });
        handleLinkedInterpretedCallEnded(store, data);
    });
    // Error handling
    queueService.on('error', (data) => {
        // An in-call invite's refusal (call full, not found…) belongs to its
        // row in the invite panel, not to the interpreter queue.
        // Nor does a refused group-call rejoin (the room page reports it).
        if (data.operation === 'conference_add' || isGroupRejoinPending()) {
            return;
        }
        // Nor does the host's end-for-all refusal (the hang-up menu reports it).
        if (data.operation === 'p2p_end_for_all') {
            return;
        }
        if (data.code === 'QUEUE_WS_CONNECTION_FAILED') {
            store.dispatch(queueConnectionChanged(false, data.message || 'Unable to connect to queue server.'));
            return;
        }
        mobileLog('error', 'interpreter_queue_service_error', {
            code: data.code,
            message: data.message,
            retrying: data.retrying
        }, { console: false });
        if (!data.retrying) {
            store.dispatch({
                type: QUEUE_SERVICE_ERROR,
                error: data.message || 'Unable to request an interpreter.'
            });
        }
    });
    console.log('[InterpreterQueue] Middleware initialized');
    return true;
}
